import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Navbar1 } from "@/components/ui/navbar-1";
import { SiteFooter } from "@/components/SiteFooter";
import { BlogMarkdown } from "@/components/BlogMarkdown";
import { getPostBySlug, listPublishedPosts, type BlogPost } from "@/lib/blog.functions";
import { CATEGORY_LABELS, type BlogCategory } from "@/lib/blog-topics";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const post = await getPostBySlug({ data: { slug: params.slug } });
    if (!post) throw notFound();
    const related = await listPublishedPosts({
      data: { category: post.category, limit: 4 },
    });
    return { post, related: related.filter((r) => r.slug !== post.slug).slice(0, 3) };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.post;
    const url = `https://viny.club/blog/${params.slug}`;
    if (!p) {
      return {
        meta: [{ title: "Blog — Viny Club" }],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const ogImage = p.cover_url ? `https://viny.club${p.cover_url}` : undefined;
    return {
      meta: [
        { title: `${p.title} | Viny Club` },
        { name: "description", content: p.excerpt },
        { name: "keywords", content: (p.keywords ?? []).join(", ") },
        { property: "og:title", content: p.title },
        { property: "og:description", content: p.excerpt },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(ogImage ? [{ property: "og:image", content: ogImage }] : []),
        ...(ogImage ? [{ name: "twitter:card", content: "summary_large_image" }] : []),
        ...(ogImage ? [{ name: "twitter:image", content: ogImage }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: p.title,
            description: p.excerpt,
            image: ogImage ? [ogImage] : undefined,
            datePublished: p.published_at,
            dateModified: p.updated_at,
            author: { "@type": "Organization", name: "Viny Club" },
            publisher: {
              "@type": "Organization",
              name: "Viny Club",
              url: "https://viny.club",
            },
            mainEntityOfPage: url,
            articleSection: CATEGORY_LABELS[p.category],
            keywords: (p.keywords ?? []).join(", "),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Início", item: "https://viny.club/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://viny.club/blog" },
              { "@type": "ListItem", position: 3, name: p.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BlogDetail,
  errorComponent: () => (
    <div className="flex min-h-screen items-center justify-center px-4 text-center">
      <div>
        <h1 className="text-2xl font-medium">Erro ao carregar</h1>
        <Link to="/blog" className="mt-4 inline-block text-sm underline">
          Voltar ao blog
        </Link>
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center px-4 text-center">
      <div>
        <h1 className="text-2xl font-medium">Artigo não encontrado</h1>
        <Link to="/blog" className="mt-4 inline-block text-sm underline">
          Ver todos os artigos
        </Link>
      </div>
    </div>
  ),
});

function BlogDetail() {
  const { post, related } = Route.useLoaderData() as {
    post: BlogPost;
    related: BlogPost[];
  };
  const publishedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 />
      <main className="mx-auto max-w-3xl px-6 pt-28 pb-16">
        <nav aria-label="Breadcrumb" className="mb-8 text-xs text-muted-foreground">
          <Link to="/" className="hover:underline">Início</Link>
          {" · "}
          <Link to="/blog" className="hover:underline">Blog</Link>
        </nav>

        <article>
          <header className="mb-10">
            <div className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              <span>{CATEGORY_LABELS[post.category]}</span>
              <span aria-hidden>·</span>
              <span>{post.reading_time} min de leitura</span>
              {publishedDate && (
                <>
                  <span aria-hidden>·</span>
                  <time dateTime={post.published_at ?? undefined}>{publishedDate}</time>
                </>
              )}
            </div>
            <h1 className="text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="mt-4 text-lg text-muted-foreground">{post.subtitle}</p>
            )}
          </header>

          {post.cover_url && (
            <div className="mb-12 aspect-[16/9] overflow-hidden rounded-2xl border border-white/10">
              <img loading="lazy" decoding="async"
                src={post.cover_url}
                alt={post.title}
                className="h-full w-full object-cover"
              />
            </div>
          )}

          <BlogMarkdown>{post.body_md}</BlogMarkdown>
        </article>

        {related.length > 0 && (
          <aside className="mt-16 border-t border-border pt-10">
            <h2 className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Continue lendo
            </h2>
            <ul className="space-y-4">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: r.slug }}
                    className="block hover:opacity-70"
                  >
                    <div className="text-base font-medium">{r.title}</div>
                    <div className="text-sm text-muted-foreground">{r.excerpt}</div>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
